"use client";

import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { motion } from "framer-motion";
import Image from "next/image";
import styles from "./multi-token-confirmation.module.scss";
import { useTokenManagerStore } from "@/features/bridge/stores/token-type.store";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import type { GeneralTokenCollectionType } from "@/features/bridge/types/open-sea-api.types";
import { CheckoutStatusType } from "@/features/bridge/types/checkout.types";
import type { SelectedMultiTokenData } from "@/features/bridge/types/method-functions.types";
import CheckoutMultiToken from "@/features/bridge/components/token-confirmation/checkout/checkout-token-multi";
import BlessButton from "@components/layout/button/green-outline-btn";
import CheckoutLoader from "@/features/bridge/components/token-confirmation/checkout/checkout-loader";
import { useBlessnetValidation } from "@/features/bridge/hooks/useBlessnetValidation.hook";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import BlessnetUnavailableModal from "@/components/layout/modal/blessnet-unavailable-modal";

interface Props {
  clearTokenSelection: () => void;
  selectedCollection: GeneralTokenCollectionType[string] | undefined;
  removeSentTokens: (tokens: any[]) => void;
}

const MultiTokenConfirmation = ({
  clearTokenSelection,
  removeSentTokens,
  selectedCollection,
}: Props) => {
  const {
    bridgeControls: {
      fromChain: { originChain },
      toChain: { destinationChain },
      isBridgePending,
      checkoutLoadingStatus,
      setCheckoutLoadingStatus,
    },
    contractManager: {
      generalAssets: { checkoutForERC721, checkoutForERC1155 },
    },
  } = useBridge();

  const currentlySelectedTokenType = useTokenManagerStore(
    (state) => state.currentlySelectedTokenType,
  );

  // Blessnet validation hook
  const {
    validateBlessnetBridging,
    isBridgingToBlessnet,
    isValidating,
    showUnavailableModal,
    closeUnavailableModal,
  } = useBlessnetValidation();

    const handleConfirm = async () => {
    // Check if we're bridging to Blessnet and validate safety requirements
    if (isBridgingToBlessnet(destinationChain)) {
      // Pass the NFT/Multi-token contract address being bridged and fallback to SHIFT_GATEWAY
      const isValidForBlessnet = await validateBlessnetBridging(
        destinationChain,
        selectedCollection?.collectionData.address, // Token contract being bridged
        CONTRACT_ADDRESSES.SHIFT_GATEWAY,           // Fallback gateway contract
        originChain?.chainId                        // Origin chain ID for self-bridging detection
      );

      if (!isValidForBlessnet) {
        // Validation failed - modal will be shown by the hook
        return;
      }
    }
    if (selectedCollection) {
      if (currentlySelectedTokenType === TokenType.NFT) {
        //$ ERC721
        const contract = selectedCollection?.collectionData.address;
        const tokenIds = selectedCollection?.tokenData
          .filter((token) => token.amountToBridge)
          .reduce(
            (acc: bigint[], token) => [...acc, BigInt(token.identifier)],
            [],
          );

        // Clear the selections immediately to prevent double-clicking and allow new selections
        clearTokenSelection();

        const isSuccess = await checkoutForERC721(
          destinationChain,
          contract,
          tokenIds,
        );

        // Only apply optimistic updates if transaction was successful
        if (isSuccess) {
          // Convert bigint[] to string[] for the store function
          const tokenIdentifiers = tokenIds.map(id => id.toString());
          console.log('🗑️ Removing NFT tokens from UI:', tokenIdentifiers);
          removeSentTokens(tokenIdentifiers);
          // setCheckoutLoadingStatus(CheckoutStatusType.Success);
        }
      } else if (currentlySelectedTokenType === TokenType.MULTI) {
        //$ ERC1155
        const contract = selectedCollection.collectionData.address;
        const selectedTokens = selectedCollection.tokenData
          .filter((token) => !!token.amountToBridge)
          .reduce(
            (acc: SelectedMultiTokenData[], token) => [
              ...acc,
              {
                id: token.identifier,
                amount: token.amountToBridge!,
              },
            ],
            [],
          );

        // Clear the selections immediately to prevent double-clicking and allow new selections
        clearTokenSelection();

        const isSuccess = await checkoutForERC1155(
          destinationChain,
          contract,
          selectedTokens,
        );

        // Only apply optimistic updates if transaction was successful
        if (isSuccess) {
          removeSentTokens(selectedTokens);
          // setCheckoutLoadingStatus(CheckoutStatusType.Success);
        }
      }
    }
  };

  // const handleCheckOutStatus = () => {
  //   setCheckoutLoadingStatus(null);
  // };

  const selectedTokens = selectedCollection?.tokenData.filter(
    (token) => token.amountToBridge,
  );

  return (
    <div className={styles.container}>
      <div className={styles.modalContent}>
        {selectedTokens?.map((token, index) => (
          <CheckoutMultiToken token={token} key={token.identifier} />
        ))}
        {!selectedTokens?.length && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={styles.noTokensSelected}
          >
            <Image
              src={require("@public/web-content/bless-thinking.png")}
              width={100}
              height={200}
              alt="no-image-placeholder"
            />
            Hmm, no tokens selected...
          </motion.div>
        )}
      </div>
      <div className={styles.buttons}>
        <BlessButton
          buttonType="outlined"
          styles={{ flexGrow: 1, paddingLeft: 0, paddingRight: 0 }}
          disabled={!selectedTokens?.length}
          onClick={clearTokenSelection}
        >
          Clear selection
        </BlessButton>
        <BlessButton
          styles={{ flex: 1 }}
          disabled={!selectedTokens?.length || isValidating}
          onClick={handleConfirm}
        >
          {isValidating ? "Validating..." : "Bridge Now"}
        </BlessButton>
      </div>
      <CheckoutLoader
        loadingStatus={isBridgePending ? CheckoutStatusType.CheckingOut : null}
        // buttonFunc={handleCheckOutStatus}
        buttonText="Close"
      />

      {/* Blessnet Unavailable Modal */}
      <BlessnetUnavailableModal
        isOpen={showUnavailableModal}
        onClose={closeUnavailableModal}
      />
    </div>
  );
};

export default MultiTokenConfirmation;
